import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SUIT_MARK, signOut, updateName, useSession } from '../lib/session';
import { setTheme, useTheme } from '../lib/theme';
import { clearPinnedClock, isClockPinned } from '../lib/time';

const BUILD = `${__APP_VERSION__} · ${__BUILD_TIME__}`;

/** Deliberately thin. Four days, then never opened again. */
export default function Profile() {
  const session = useSession();
  const navigate = useNavigate();
  const { choice } = useTheme();
  const [name, setName] = useState(session?.name ?? '');

  if (!session) return null;

  const providerLine = session.provider === 'google'
    ? 'You signed in with Google.'
    : session.provider === 'admin'
      ? 'You signed in as an organiser.'
      : 'You signed in with a code sent to your email.';

  return (
    <div className="page page-pad page-narrow page-narrow-sm" style={{ paddingTop: 20 }}>
      <h2 style={{ margin: '0 0 16px' }}>You</h2>

      <div className="field">
        <label htmlFor="name">Your name, as it shows on your photos</label>
        <input
          id="name" className="input" style={{ minHeight: 48, fontSize: 16 }}
          value={name} onChange={(e) => setName(e.target.value)} onBlur={() => updateName(name)}
        />
      </div>

      <div className="card" style={{ marginTop: 16, background: 'var(--color-accent-2-100)', gap: 6 }}>
        <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--color-accent-2-800)' }}>
          Your team
        </div>
        <div style={{ fontFamily: 'var(--font-heading)', fontSize: 28, color: 'var(--color-accent-2-900)' }}>
          <span aria-hidden="true">{SUIT_MARK[session.team]}</span> {session.team}
        </div>
      </div>

      <div className="card" style={{ marginTop: 12, gap: 4 }}>
        <div style={{ fontSize: 13 }}>{providerLine}</div>
        <div style={{ fontSize: 13, opacity: 0.75 }}>
          Come back on another phone with <strong>{session.email}</strong> and everything of yours is still yours.
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <div className="field"><label>Light or dark</label></div>
        <div className="seg">
          {(['system', 'light', 'dark'] as const).map((option) => (
            <label key={option} className="seg-opt">
              <input type="radio" name="theme" checked={choice === option} onChange={() => setTheme(option)} />
              {option === 'system' ? 'Match my phone' : option === 'light' ? 'Light' : 'Dark'}
            </label>
          ))}
        </div>
      </div>

      {session.isAdmin && (
        /* Organisers only — group membership on an ordinary account (D48).
           D37 says no admin console and this is still not one: hiding happens
           on a photo's own screen, and the guest list below is read-only. */
        <div className="card" style={{ marginTop: 16, background: 'var(--color-surface)', gap: 8 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', opacity: 0.6 }}>
            Organiser
          </div>
          <p style={{ fontSize: 13, margin: 0, opacity: 0.85 }}>
            You can hide and unhide any photo from its own screen, and the gallery has a Hidden filter.
            You cannot delete anything — that stays with whoever took it.
          </p>
          <p style={{ fontSize: 13, margin: 0, opacity: 0.85 }}>
            The reception slideshow starts from the <Link to="/gallery">Photos</Link> tab.
          </p>
          <Link
            className="btn btn-secondary" to="/me/guests"
            style={{ minHeight: 44, marginTop: 4, background: 'var(--color-bg)' }}
          >
            Who has signed in
          </Link>
        </div>
      )}

      <div className="stack" style={{ gap: 10, marginTop: 24 }}>
        <Link to="/credits" style={{ fontSize: 14 }}>Everyone who made this week</Link>
        <p style={{ fontSize: 12, opacity: 0.55, margin: 0 }}>
          You can add this page to your home screen from your browser's share menu, if you'd rather not go
          hunting for it in a tab. Entirely optional.
        </p>
      </div>

      <button
        type="button" className="btn btn-secondary btn-block" style={{ minHeight: 48, marginTop: 24 }}
        onClick={() => { signOut(); navigate('/', { replace: true }); }}
      >
        Sign out
      </button>

      {isClockPinned() && (
        <button type="button" className="btn btn-ghost btn-block" onClick={() => { clearPinnedClock(); window.location.reload(); }}>
          The clock is pinned for a demo — put it back
        </button>
      )}

      <p style={{ fontSize: 11, opacity: 0.4, marginTop: 20, textAlign: 'center' }}>{BUILD}</p>
    </div>
  );
}
